/**
 * PermissionGridCategoryRow Component
 * Collapsible category header row that shows aggregate permission counts
 */

import React, { memo } from 'react';
import { colors } from '../../theme';
import { LockedBadge } from '../base/Locked';

export interface CategoryAggregates {
  /** Total permissions in this category */
  total: number;
  /** Count of enabled permissions per role: roleId -> count */
  perRole: Record<string, number>;
  /** Permissions in this category the current user can't give. */
  locked?: number;
}

export interface PermissionGridCategoryRowProps {
  category: string;
  aggregates: CategoryAggregates;
  roleIds: string[];
  isExpanded: boolean;
  onToggle: () => void;
}

/**
 * Collapsible category row showing category name and aggregate counts per role
 */
export const PermissionGridCategoryRow = memo(function PermissionGridCategoryRow({
  category,
  aggregates,
  roleIds,
  isExpanded,
  onToggle,
}: PermissionGridCategoryRowProps) {
  return (
    <tr
      onClick={onToggle}
      style={{
        cursor: 'pointer',
        backgroundColor: colors.background.tertiary,
        transition: 'background-color 0.15s ease',
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.backgroundColor = colors.background.secondary;
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.backgroundColor = colors.background.tertiary;
      }}
    >
      {/* Category name cell (sticky left) */}
      <td
        style={{
          position: 'sticky',
          left: 0,
          backgroundColor: colors.background.tertiary,
          padding: '10px 12px',
          borderBottom: `1px solid ${colors.border.default}`,
          borderRight: `1px solid ${colors.border.default}`,
          fontWeight: 600,
          color: colors.text.primary,
          zIndex: 1,
          minWidth: '200px',
          transition: 'background-color 0.15s ease',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '20px',
              height: '20px',
              fontSize: '12px',
              color: colors.text.secondary,
              transition: 'transform 0.2s ease',
              transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
            }}
          >
            &#9658;
          </span>
          <span>{category}</span>
          <span
            style={{
              fontSize: '12px',
              color: colors.text.secondary,
              fontWeight: 400,
            }}
          >
            ({aggregates.total})
          </span>
          {!!aggregates.locked && <LockedBadge text={`${aggregates.locked} can't give`} testId="permission-category-locked-count" />}
        </div>
      </td>

      {/* Aggregate counts per role */}
      {roleIds.map((roleId) => {
        const count = aggregates.perRole[roleId] || 0;
        const isAllEnabled = count === aggregates.total;
        const isSomeEnabled = count > 0 && count < aggregates.total;
        const isNoneEnabled = count === 0;

        // Background color: green for all, yellow/amber for some, transparent for none
        const backgroundColor = isAllEnabled
          ? 'rgba(34, 197, 94, 0.25)' // Green for all permissions
          : isSomeEnabled
            ? 'rgba(234, 179, 8, 0.2)' // Yellow/amber for some permissions
            : 'transparent';

        // Text color to match background
        const textColor = isAllEnabled
          ? '#22c55e' // Green
          : isSomeEnabled
            ? '#eab308' // Yellow
            : colors.text.tertiary;

        return (
          <td
            key={roleId}
            style={{
              padding: '10px 8px',
              textAlign: 'center',
              borderBottom: `1px solid ${colors.border.default}`,
              borderRight: `1px solid ${colors.border.default}`,
              color: textColor,
              fontSize: '12px',
              fontWeight: isAllEnabled ? 600 : isSomeEnabled ? 500 : 400,
              backgroundColor,
            }}
          >
            {count}/{aggregates.total}
          </td>
        );
      })}
    </tr>
  );
});

export default PermissionGridCategoryRow;
